/**
 * @file StrategiesGainLoss.jsx
 * @description Main application interface with role-specific features
 * 
 * @section Responsibilities
 * - Manage role-based views
 * - Handle data aggregation
 * - Control real-time updates
 * - Manage user preferences
 * 
 * @section Dependencies
 * - Role Management: Access control
 * - Data Services: Information handling
 * - State Management: Global state
 * 
 * @section Integration Points
 * - Data API: Information retrieval
 * - Role System: Access control
 * - Notification System: Real-time updates
 * 
 * @section State Management
 * - dashboardData: Aggregated information
 * - loadingState: Data fetching
 * - errorState: Error handling
 * 
 * @section Events
 * - onDataUpdate: Data changes
 * - onError: Error handling
 * - onPreferenceChange: Settings updates
 * 
 * @section Props
 * @prop {String} userRole - Current user role
 * @prop {Object} permissions - User permissions
 */

import React, { useState, useEffect } from "react";
import Widget from "../Widget";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import Loader from "../Loader";
import { useNavigate } from "react-router";
import { formatAmount } from "../../../../utils/formatAmount";

const dataKey = "Gain Loss %";

function StrategiesGainLoss({ width, data, loading, strategies = [] }) {
  const [strategiesData, setStrategiesData] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    if (data) {
      const tmpStrategies = data.map((strat) => ({
        [dataKey]: strat?.performance?.performancePerc?.toFixed(2) || 0,
      }));
      setStrategiesData(tmpStrategies);
    }
  }, [data]);

  return (
    <Widget name={"strategies-gain-loss"} width={"full-width"}>
      <div className="heading">
        <h1 className="title">Strategies & Gain/Loss</h1>
      </div>
      {loading ? (
        <Loader />
      ) : (
        <ResponsiveContainer width="100%" height={360}>
          <BarChart width={300} height={300} data={strategiesData}>
            <defs>
              <linearGradient id="positiveGradient" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#FBED96" stopOpacity={0.8} />{" "}
                {/* Greenish gradient top */}
                <stop
                  offset="100%"
                  stopColor="#ABECD6"
                  stopOpacity={0.8}
                />{" "}
                {/* Greenish gradient bottom */}
              </linearGradient>
              <linearGradient id="negativeGradient" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#EC008C" stopOpacity={0.8} />{" "}
                {/* Reddish gradient top */}
                <stop
                  offset="100%"
                  stopColor="#FC6767"
                  stopOpacity={0.8}
                />{" "}
                {/* Reddish gradient bottom */}
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="2 2"
              horizontal={false}
              stroke="#95B2C5"
            />
            <XAxis dataKey="symbol" stroke="#95B2C5" />
            <YAxis stroke="#95B2C5" />
            <Tooltip />
            <ReferenceLine y={0} stroke="#ffffff" />
            <Bar
              dataKey={dataKey}
              fill="#8884d8"
              radius={[30, 30, 0, 0]}
              barSize={52}
            >
              {strategiesData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    entry.gainLossPerc > 0
                      ? "url(#positiveGradient)"
                      : "url(#negativeGradient)"
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
      <table className="basic-table strategies-table">
        <thead>
          <tr>
            <th>Strategy</th>
            <th># Clients</th>
            <th>Risk</th>
            <th>Performance %</th>
          </tr>
        </thead>
        {strategies.map((s) => (
          <tr
            onClick={() => {
              navigate("/strategies/strategy/" + s.strategyId);
            }}
          >
            <td style={{ textAlign: "center" }}>{s.name}</td>
            <td>{s.clientsNo}</td>
            <td>{formatAmount(s.riskScore, 0)}</td>
            <td
              style={{
                color:
                  s.performance.performancePerc >= 0 ? "#10b981" : "#ef4444",
              }}
            >
              {s.performance.performancePerc >= 0 ? "+" : ""}{" "}
              {formatAmount(s.performance.performancePerc, 0)} %
            </td>
          </tr>
        ))}
      </table>
    </Widget>
  );
}

export default StrategiesGainLoss;
